import { CircleAlert, Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import type { SpellCheckStatus } from '../../types/spell-check-status'

export interface SpellCheckStatusLineProps {
  /** Any status except `ready`: the line only shows while the check runs or fails. */
  status: Exclude<SpellCheckStatus, 'ready'>
  onRetry: () => void
}

/** The first failure and the retry in progress say the same thing; only a failed retry escalates. */
const ERROR_COPY: Record<Exclude<SpellCheckStatus, 'ready' | 'loading'>, string> = {
  error: 'Can’t check spelling.',
  retrying: 'Can’t check spelling.',
  unavailable: 'Spell check unavailable.',
}

/**
 * Status line at the top of the results while the check runs or fails (Figma "Check Spelling
 * Panel / Status"). Checking is a spinner and text. Error, Retrying and Retry failed are a
 * destructive alert with a Retry button; the button shows a spinner and is disabled while the retry
 * runs, and the copy escalates when the retry fails.
 */
export function SpellCheckStatusLine({ status, onRetry }: SpellCheckStatusLineProps) {
  if (status === 'loading') {
    return (
      <p
        role="status"
        className="flex items-center gap-2 text-sm leading-none text-muted-foreground"
      >
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Checking spelling…
      </p>
    )
  }

  return (
    <Alert variant="destructive" className="border-destructive/40 bg-destructive/5">
      <CircleAlert className="size-4" aria-hidden="true" />
      <div className="flex items-center justify-between gap-3">
        <AlertDescription className="text-sm leading-snug font-medium">
          {ERROR_COPY[status]}
        </AlertDescription>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          isLoading={status === 'retrying'}
          onClick={onRetry}
        >
          Retry
        </Button>
      </div>
    </Alert>
  )
}
